import { getFileUrl } from '@/clients/http'
import { IdCard, Mail, NotebookPen, Phone, User as UserIcon, Wallet } from 'lucide-react'
import { type PriceBook, PriceBookModel } from '@/http/models/PosCatalog'
import { Image as ImageIcon } from 'lucide-react'
import { useForm } from 'react-hook-form'

import { useEffect, useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

import { TextInput, SwitchInput } from '@/components/atoms'
import { SelectInput } from '@/components/atoms/inputs/SelectInput'
import { useAuth } from '@/contexts/AuthContext'
import { MediaGalleryModal } from '@/components/media/MediaGalleryModal'

import { useFeatureFlags } from '@/contexts/FeatureFlagsContext'

import { CustomerModel as CustomerType } from '@/types'

import { CustomerFormState, customerFieldsConfig } from './customerFieldsConfig'
import { useEnterSubmit } from '@/hooks/ui'
import { Spinner } from '@/components/ui/spinner'

interface CustomerDialogProps {
  open: boolean
  mode: 'create' | 'edit' | 'view'
  editCustomer: CustomerType.Customer | null
  closeDialog: () => void
  onCreate: (data: CustomerFormState) => void
  onUpdate: (id: string, data: CustomerFormState) => void
  loading?: boolean
}

export const CustomerDialog = ({
  open,
  mode,
  editCustomer,
  closeDialog,
  onCreate,
  onUpdate,
  loading = false,
}: CustomerDialogProps) => {
  const [galleryOpen, setGalleryOpen] = useState(false)
  // POS credit fields only for tenants with the POS channel, and only when editing (create does not take them).
  const { isFeatureEnabled } = useFeatureFlags()
  const showCredit = mode !== 'create' && isFeatureEnabled('pos')
  const { can } = useAuth()
  const showBook = isFeatureEnabled('pos') && can('price_books:list')
  const [books, setBooks] = useState<PriceBook[]>([])
  useEffect(() => {
    if (!open || !showBook) return
    PriceBookModel.getList({ length: 200 }).then((res) => {
      if (res.success && res.data) setBooks(res.data.result.filter((b) => b.status === 'active' || !b.status))
    })
  }, [open, showBook])
  const {
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
    control,
  } = useForm<CustomerFormState>({
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      status: 'active',
      image_id: '',
      image_url_temp: '',
    },
  })

  const { fields } = customerFieldsConfig()
  const formValues = watch()
  const status = watch('status')

  useEffect(() => {
    if (open) {
      if ((mode === 'edit' || mode === 'view') && editCustomer) {
        reset({
          name: editCustomer.name,
          email: editCustomer.email || '',
          phone: editCustomer.phone || '',
          status: editCustomer.status === 'active' ? 'active' : 'inactive',
          image_id: editCustomer.image_id || '',
          image_url_temp: editCustomer.image?.path || '',
          barcode: editCustomer.barcode || '',
          price_book_id: editCustomer.price_book_id || 'none',
          credit_limit: String(editCustomer.credit_limit ?? 0),
          offline_credit_allowance:
            editCustomer.offline_credit_allowance === null || editCustomer.offline_credit_allowance === undefined
              ? ''
              : String(editCustomer.offline_credit_allowance),
        })
      } else {
        reset({
          name: '',
          email: '',
          phone: '',
          status: 'active',
          image_id: '',
          image_url_temp: '',
          price_book_id: 'none',
        })
      }
    }
  }, [open, mode, editCustomer, reset])

  const onSubmit = async (data: CustomerFormState) => {
    // Ensure image is treated as ID
    const imageId = data.image_id

    // Clean up empty fields
    const payload: Record<string, unknown> = { ...data, image_id: imageId }
    delete payload.image_url_temp
    if (!payload.email) delete payload.email
    if (!payload.phone) delete payload.phone
    delete payload.credit_limit
    delete payload.offline_credit_allowance
    delete payload.price_book_id
    // Sent only when it changed, so saving other fields never touches the customer's book.
    const book = data.price_book_id && data.price_book_id !== 'none' ? data.price_book_id : null
    if (showBook && book !== (editCustomer?.price_book_id ?? null) && (mode !== 'create' || book)) payload.price_book_id = book
    // Card: sent when set, or as null to remove one the customer had.
    const card = String(data.barcode ?? '').trim()
    delete payload.barcode
    if (card) payload.barcode = card
    else if (mode !== 'create' && editCustomer?.barcode) payload.barcode = null
    // Sent only when the loaded record had credit fields, so a partial record never zeroes a real limit.
    if (showCredit && editCustomer?.credit_limit !== undefined) {
      payload.credit_limit = Number(data.credit_limit) || 0
      const allowance = String(data.offline_credit_allowance ?? '').trim()
      payload.offline_credit_allowance = allowance === '' ? null : Number(allowance)
    }

    if (mode === 'create') {
      onCreate(payload as unknown as CustomerFormState)
    } else {
      if (!editCustomer?._id) return
      onUpdate(editCustomer._id, payload as unknown as CustomerFormState)
    }
  }

  useEnterSubmit(handleSubmit(onSubmit), open && mode !== 'view')

  return (
    <>
      <Dialog open={open} onOpenChange={closeDialog}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>
              {mode === 'create'
                ? 'Add customer'
                : mode === 'edit'
                  ? `Edit ${editCustomer?.name ?? 'customer'}`
                  : 'Customer details'}
            </DialogTitle>
            <DialogDescription>
              {mode === 'view'
                ? 'View customer details.'
                : 'Manage customer profile information.'}
            </DialogDescription>
          </DialogHeader>

          <div className="flex justify-center py-2">
            <div className="w-full max-w-60">
              <div className="flex min-h-40 flex-col items-center justify-center rounded-lg border border-dashed border-border bg-subdued p-4">
                {formValues.image_url_temp ? (
                  <div className="relative w-full aspect-square overflow-hidden rounded-full group">
                    <img
                      src={getFileUrl(formValues.image_url_temp as string)}
                      alt="Customer Profile"
                      className="size-full object-cover"
                    />

                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => setGalleryOpen(true)}
                      >
                        Change
                      </Button>
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={() => {
                          setValue('image_url_temp', '')
                          setValue('image_id', '')
                        }}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2 text-center">
                    <span className="inline-grid size-12 place-items-center rounded-full bg-muted">
                      <ImageIcon className="size-5 text-muted-foreground" />
                    </span>
                    <p className="text-sm text-text-2">No image</p>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => setGalleryOpen(true)}
                      disabled={mode === 'view'}
                    >
                      Select image
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-4 py-2">
            <TextInput
              control={control}
              name="name"
              label="Full name"
              placeholder="Customer name"
              disabled={mode === 'view'}
              rules={fields.name.rules}
              errors={errors}
              icon={<UserIcon className="size-4" />}
            />

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <TextInput
                control={control}
                name="email"
                label="Email"
                placeholder="customer@example.com"
                disabled={mode === 'view'}
                rules={fields.email.rules}
                errors={errors}
                icon={<Mail className="size-4" />}
              />

              <TextInput
                control={control}
                name="phone"
                label="Phone"
                placeholder="017XXXXXXXX"
                disabled={mode === 'view'}
                rules={fields.phone.rules}
                errors={errors}
                icon={<Phone className="size-4" />}
              />
            </div>

            {isFeatureEnabled('pos') && (
              <TextInput
                control={control}
                name="barcode"
                label="Customer card"
                placeholder="Scan or type the card number"
                disabled={mode === 'view'}
                errors={errors}
                icon={<IdCard className="size-4" />}
              />
            )}

            {showCredit && (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <TextInput
                  control={control}
                  name="credit_limit"
                  label="Credit limit"
                  placeholder="0"
                  disabled={mode === 'view'}
                  rules={{ pattern: { value: /^\d+(\.\d+)?$/, message: 'Enter an amount' } }}
                  errors={errors}
                  icon={<Wallet className="size-4" />}
                />
                <TextInput
                  control={control}
                  name="offline_credit_allowance"
                  label="Offline credit allowance"
                  placeholder="Store default (0 if not set)"
                  disabled={mode === 'view'}
                  rules={{ pattern: { value: /^\d+(\.\d+)?$/, message: 'Enter an amount, or leave empty' } }}
                  errors={errors}
                  icon={<NotebookPen className="size-4" />}
                />
              </div>
            )}

            {showBook && (
              <div className="space-y-1">
                <SelectInput
                  control={control}
                  name="price_book_id"
                  label="Price book"
                  disabled={mode === 'view'}
                  options={[
                    { label: 'None (normal prices)', value: 'none' },
                    ...books.map((b) => ({ label: b.title, value: b._id })),
                    ...(editCustomer?.price_book_id && !books.some((b) => b._id === editCustomer.price_book_id)
                      ? [{ label: 'A deleted or inactive book', value: editCustomer.price_book_id }]
                      : []),
                  ]}
                />
                <p className="text-xs text-muted-foreground">The till charges this customer the book's prices where the branch has them.</p>
              </div>
            )}

            <SwitchInput
              control={control}
              name="status"
              label="Account status"
              description={formValues.status === 'active'
                ? 'Customer account is active.'
                : 'Customer account is inactive.'}
              disabled={mode === 'view'}
              activeValue="active"
              inactiveValue="inactive"
            />
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={closeDialog} disabled={loading} className="w-full sm:w-auto">
              {mode === 'view' ? 'Close' : 'Cancel'}
            </Button>
            {mode !== 'view' && (
              <Button type="button" disabled={loading} onClick={handleSubmit(onSubmit)} className="w-full sm:w-auto">
                {loading && <Spinner />}
                {mode === 'create' ? 'Add customer' : 'Save'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MediaGalleryModal
        open={galleryOpen}
        onOpenChange={setGalleryOpen}
        onSelect={(files) => {
          if (files.length > 0) {
            setValue('image_url_temp', files[0].url)
            setValue('image_id', files[0].id)
          }
        }}
      />
    </>
  )
}
